import { usePageTitle } from '../hooks/usePageTitle';

export default function TrustCenter() {
  usePageTitle("Trust Center — Chuck's Mojito Shack");

  return (
    <div className="container page-content py-5" style={{ maxWidth: '820px' }}>
      <div className="mb-4">
        <span className="badge bg-success me-2">Enterprise Grade</span>
        <span className="badge bg-primary me-2">Cloud Native</span>
        <span className="badge bg-secondary">Military Strength*</span>
      </div>

      <h1 className="mb-1">Trust Center</h1>
      <p className="text-muted mb-2">
        <small>* "Military strength" refers to the rum.</small>
      </p>
      <p className="lead mb-5">
        Chuck's Mojito Shack is built on a foundation of cutting-edge cloud security architecture,
        industry-standard protocols, and at least two passwords that aren't <code>password123</code>
        . This page documents our formidable security posture so that you can order a drink with
        confidence.
      </p>

      {/* ===== PILLAR 1 ===== */}
      <div className="card mb-4 border-success">
        <div className="card-header bg-success text-white fw-semibold">
          &#x1F6E1;&#xFE0F; Zero Backend Attack Surface
        </div>
        <div className="card-body">
          <p>
            Chuck's Mojito Shack runs as a{' '}
            <strong>100% client-side React Single-Page Application</strong> compiled to static
            files. There is no application server. There are no API endpoints. There is no backend
            to breach, no database to exfiltrate, and no server-side code to exploit.
          </p>
          <p>
            This is not a cost-cutting measure. It is a{' '}
            <em>deliberate zero-trust perimeter elimination strategy</em>. You cannot attack a
            server that does not exist. Chuck read half of a threat modeling article and arrived at
            this conclusion independently.
          </p>
          <p className="mb-0 text-muted">
            <small>Technology: React 19 + TypeScript, built to static assets with Vite</small>
          </p>
        </div>
      </div>

      {/* ===== PILLAR 2 ===== */}
      <div className="card mb-4 border-primary">
        <div className="card-header bg-primary text-white fw-semibold">
          &#x2601;&#xFE0F; Microsoft Entra ID — Enterprise Identity
        </div>
        <div className="card-body">
          <p>
            Authentication is delegated entirely to <strong>Microsoft Entra ID</strong> (formerly
            Azure Active Directory), the same identity platform trusted by Fortune 500 companies,
            government agencies, and organizations with significantly larger security budgets than a
            seasonal tiki bar.
          </p>
          <p>
            Chuck does not store your password. Chuck does not see your password. Chuck cannot reset
            your password from the bar. All credential management, MFA enforcement, and suspicious
            sign-in detection is handled by a team of Microsoft engineers who are, statistically
            speaking, more awake than Chuck at last call.
          </p>
          <p className="mb-0 text-muted">
            <small>Technology: Microsoft Entra ID (Azure AD), OpenID Connect 1.0</small>
          </p>
        </div>
      </div>

      {/* ===== PILLAR 3 ===== */}
      <div className="card mb-4 border-info">
        <div className="card-header bg-info text-white fw-semibold">
          &#x1F510; OpenID Connect — Industry-Standard Authentication Protocol
        </div>
        <div className="card-body">
          <p>
            Login is implemented via <strong>OpenID Connect (OIDC)</strong>, the industry-standard
            identity layer built on OAuth 2.0. Authentication tokens are issued by Entra ID,
            cryptographically signed, and validated by the browser. No credentials ever transit
            through our servers because, again, we do not have servers.
          </p>
          <p>
            The OIDC authorization code flow with PKCE (Proof Key for Code Exchange) protects
            against interception attacks. This is the same flow used by Google, Apple, and every
            other company whose security team outnumbers their bar staff. PKCE is pronouned "pixie,"
            which we find charming.
          </p>
          <p className="mb-0 text-muted">
            <small>
              Technology: OIDC via <code>@azure/msal-browser</code> (Microsoft Authentication
              Library)
            </small>
          </p>
        </div>
      </div>

      {/* ===== PILLAR 4 ===== */}
      <div className="card mb-4 border-warning">
        <div className="card-header bg-warning fw-semibold">
          &#x1F4E6; Browser Sandbox &amp; Same-Origin Isolation
        </div>
        <div className="card-body">
          <p>
            Application logic runs inside the <strong>browser's JavaScript sandbox</strong> — an
            execution environment that prevents our code from reaching anything outside its own
            origin. This is not a Chuck invention. This is how browsers work. We are simply
            benefiting from it and taking full credit.
          </p>
          <p>
            Every asset is served over HTTPS with correct MIME types and compressed in transit.
            Token storage is scoped to this origin and unreadable by any other site. Nothing the
            application does can reach outside that boundary without going through standard browser
            APIs. Chuck finds this deeply reassuring.
          </p>
          <p className="mb-0 text-muted">
            <small>
              Technology: Same-origin policy, IIS HTTP Compression, MIME type enforcement
            </small>
          </p>
        </div>
      </div>

      {/* ===== PILLAR 5 ===== */}
      <div className="card mb-4 border-secondary">
        <div className="card-header bg-secondary text-white fw-semibold">
          &#x1F4CA; Asset Integrity Fingerprinting
        </div>
        <div className="card-body">
          <p>
            Every static asset — JavaScript, CSS, source maps — is deployed with a{' '}
            <strong>content hash</strong> embedded in its filename by Vite's production build
            pipeline. If an asset changes by even a single byte, its filename changes, and stale
            cached versions are automatically invalidated.
          </p>
          <p>
            This means you are always running the exact code we deployed, not a cached version from
            last Tuesday that might have a bug Chuck fixed after his third mojito. Supply-chain
            cache poisoning is not a concern. Neither is Chuck's short-term memory, for different
            reasons.
          </p>
          <p className="mb-0 text-muted">
            <small>Technology: Vite content-hashed asset filenames (Rollup output hashing)</small>
          </p>
        </div>
      </div>

      {/* ===== PILLAR 6 ===== */}
      <div className="card mb-4 border-dark">
        <div className="card-header bg-dark text-white fw-semibold">
          &#x1F517; TLS Everywhere (HTTPS Only)
        </div>
        <div className="card-body">
          <p>
            All traffic to Chuck's Mojito Shack is encrypted in transit via{' '}
            <strong>Transport Layer Security (TLS)</strong>. Plaintext HTTP is not available, not
            supported, and not something Chuck is willing to discuss. Your session tokens,
            authentication flows, and drink preferences travel across the internet wrapped in
            industry-standard cryptography.
          </p>
          <p>
            Man-in-the-middle attacks are therefore impractical. Eavesdropping is futile. The only
            person listening to your traffic is TLS, and TLS does not gossip.
          </p>
          <p className="mb-0 text-muted">
            <small>Technology: TLS 1.2+ enforced at the hosting layer</small>
          </p>
        </div>
      </div>

      {/* ===== PILLAR 7 ===== */}
      <div className="card mb-4" style={{ borderColor: '#6f42c1' }}>
        <div
          className="card-header text-white fw-semibold"
          style={{ backgroundColor: '#6f42c1' }}
        >
          &#x1F500; SPA Routing via IIS URL Rewrite
        </div>
        <div className="card-body">
          <p>
            Deep-link navigation is handled by an <strong>IIS URL Rewrite rule</strong> that routes
            all non-file, non-directory requests to <code>index.html</code>. This means no route
            enumeration, no directory traversal, and no accidental exposure of file system
            structure.
          </p>
          <p>
            What the outside world sees is a single HTML file and a collection of static assets.
            This is, architecturally speaking, the minimum possible information disclosure. Security
            through minimalism. Chuck calls it "the fewer files, the fewer problems" doctrine.
          </p>
          <p className="mb-0 text-muted">
            <small>
              Technology: IIS URL Rewrite Module, <code>web.config</code> SPA fallback rule
            </small>
          </p>
        </div>
      </div>

      {/* ===== COMPLIANCE ===== */}
      <h2 className="mt-5 mb-3">Compliance &amp; Certifications</h2>
      <div className="table-responsive">
        <table className="table table-bordered">
          <thead className="table-light">
            <tr>
              <th>Standard / Framework</th>
              <th>Status</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>SOC 2 Type II</td>
              <td>
                <span className="badge bg-warning text-dark">In Progress</span>
              </td>
              <td>Chuck is reviewing what this involves.</td>
            </tr>
            <tr>
              <td>ISO 27001</td>
              <td>
                <span className="badge bg-warning text-dark">Planned</span>
              </td>
              <td>Penciled in for after peak mojito season.</td>
            </tr>
            <tr>
              <td>GDPR</td>
              <td>
                <span className="badge bg-secondary">Good Faith Effort</span>
              </td>
              <td>We read the Wikipedia article. Twice.</td>
            </tr>
            <tr>
              <td>OWASP Top 10</td>
              <td>
                <span className="badge bg-success">Largely Addressed</span>
              </td>
              <td>No backend means most of the list simply does not apply.</td>
            </tr>
            <tr>
              <td>Penetration Testing</td>
              <td>
                <span className="badge bg-info text-dark">Continuous</span>
              </td>
              <td>Chuck's nephew tries things sometimes. Nothing has happened yet.</td>
            </tr>
            <tr>
              <td>Microsoft Entra ID SLA</td>
              <td>
                <span className="badge bg-success">99.99% Uptime</span>
              </td>
              <td>Microsoft's SLA, not ours. We are riding their coattails proudly.</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ===== FOOTER ===== */}
      <div className="alert alert-light border mt-5">
        <strong>Security concerns?</strong> Contact us at{' '}
        <a href="mailto:security@chucksmojitoshack.com">security&#64;chucksmojitoshack.com</a>. For
        responsible disclosure of vulnerabilities, please include a detailed report and your
        preferred mojito flavor so we can thank you properly.
        <br />
        <br />
        <small className="text-muted">
          Chuck's Mojito Shack Trust Center is updated whenever something significant changes, or
          whenever someone asks about it and we remember this page exists. Last reviewed: March
          2026.
        </small>
      </div>
    </div>
  );
}
